import * as THREE from "three";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader";
import * as robotoFont from "../../fonts/Roboto Medium_Regular.json";
import { LoopSubdivision } from "three-subdivide";
import Bender from "./beacon-text-bending";

export function findModelObjectsByName(model: THREE.Object3D, name: string): THREE.Object3D[] {
    function find(object: THREE.Object3D, name: string): THREE.Object3D[] {
        const matchedObjects = object.name == name ? [object] : [];

        const childNodes = object.children.flatMap(childObject => { return find(childObject, name); });

        return matchedObjects.concat(childNodes);
    }

    return find(model, name).flatMap(childObject => { return childObject; });
}

export function findModelObjectsByPrefix(model: THREE.Object3D, namePrefix: string): THREE.Object3D[] {
    function find(object: THREE.Object3D, namePrefix: string): THREE.Object3D[] {
        const matchedObjects = object.name.startsWith(namePrefix) ? [object] : [];

        const childObjects = object.children.flatMap(childObject => { return find(childObject, namePrefix); });

        return matchedObjects.concat(childObjects);
    }

    return find(model, namePrefix).flatMap(childObject => { return childObject; });
}

export function findModelMeshesByName(model: THREE.Object3D, name: string): THREE.Mesh[] {
    return findModelObjectsByName(model, name).flatMap(object => { return object as THREE.Mesh; });
}

export function findModelMeshesByPrefix(model: THREE.Object3D, namePrefix: string): THREE.Mesh[] {
    return findModelObjectsByPrefix(model, namePrefix).flatMap(object => { return object as THREE.Mesh; });
}

export function modelObjectHierarchy(model: THREE.Object3D, name: string): THREE.Object3D[] {
    function hierarchy(object: THREE.Object3D): THREE.Object3D[] {
        const childObjects = object.children.flatMap(childObject => { return hierarchy(childObject); });

        return [object].concat(childObjects);
    }

    return findModelObjectsByName(model, name).flatMap(object => { return hierarchy(object); });
}

export function modelMeshHierarchy(model: THREE.Object3D, name: string): THREE.Mesh[] {
    return modelObjectHierarchy(model, name).flatMap(object => { return object as THREE.Mesh; });
}

export function smoothGeometry(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
    const iterations = 1;

    const params = {
        split: true,
        uvSmooth: false,
        preserveEdges: false,
        flatOnly: false,
        maxTriangles: Infinity,
    };

    return LoopSubdivision.modify(geometry, iterations, params);
}

export function addTextMesh(properties: { mesh: THREE.Mesh, name: string, heightPercent: number }) {
    const mesh = properties.mesh;

    mesh.material = new THREE.MeshBasicMaterial({ opacity: 0, transparent: true });

    const loader = new FontLoader();
    
    const font = loader.parse(JSON.parse(JSON.stringify(robotoFont)));

    const textGeometry = new TextGeometry(
        properties.name,
        {
            font: font,
            size: 1,
            height: 0.02
        }
    );

    textGeometry.center();
    textGeometry.rotateX(-Math.PI / 2);

    mesh.geometry.computeBoundingBox();
    textGeometry.computeBoundingBox();

    const meshHeight = (mesh.geometry.boundingBox!.max.z - mesh.geometry.boundingBox!.min.z) * properties.heightPercent;
    const textHeight = textGeometry.boundingBox!.max.z - textGeometry.boundingBox!.min.z;

    const meshWidth = mesh.geometry.boundingBox!.max.x - mesh.geometry.boundingBox!.min.x;
    const textWidth = textGeometry.boundingBox!.max.x - textGeometry.boundingBox!.min.x;

    const scaleFactorWidth = meshWidth / textWidth;
    const scaleFactorHeight = meshHeight / textHeight;
    const scaleFactor = Math.min(scaleFactorWidth, scaleFactorHeight);
    textGeometry.scale(scaleFactor, scaleFactor, scaleFactor);

    const textMaterial = new THREE.MeshPhongMaterial(
        { color: 0x000000, specular: 0x000000 }
    );

    const textMesh = new THREE.Mesh(textGeometry, textMaterial);

    mesh.add(textMesh);
}

export function addCurvedTextMesh(properties: { mesh: THREE.Mesh, name: string }) {
    const mesh = properties.mesh;

    mesh.material = new THREE.MeshBasicMaterial({ opacity: 0, transparent: true });

    const meshBoundingBox = mesh.geometry.boundingBox!;

    const width = meshBoundingBox.max.x - meshBoundingBox.min.x;

    const loader = new FontLoader();

    const font = loader.parse(JSON.parse(JSON.stringify(robotoFont)));

    const textGeometry = new TextGeometry(
        properties.name,
        {
            font: font,
            size: 1,
            height: 0.02
        }
    );

    textGeometry.center();
    textGeometry.rotateX(Math.PI / 2);
    textGeometry.rotateY(Math.PI);

    mesh.geometry.computeBoundingBox();
    textGeometry.computeBoundingBox();

    const meshHeight = (mesh.geometry.boundingBox!.max.z - mesh.geometry.boundingBox!.min.z) * 0.5;
    const textHeight = textGeometry.boundingBox!.max.z - textGeometry.boundingBox!.min.z;

    const meshWidth = (mesh.geometry.boundingBox!.max.x - mesh.geometry.boundingBox!.min.x) * 0.95;
    const textWidth = textGeometry.boundingBox!.max.x - textGeometry.boundingBox!.min.x;

    // The width of the curved mesh is the diameter of the arc
    const scaleFactorWidth = (Math.PI * meshWidth / 2) / textWidth;
    const scaleFactorHeight = meshHeight / textHeight;
    const scaleFactor = Math.min(scaleFactorWidth, scaleFactorHeight);
    textGeometry.scale(scaleFactor, scaleFactor, scaleFactor);

    const textMaterial = new THREE.MeshPhongMaterial(
        { color: 0x000000, specular: 0x000000 }
    );

    const textMesh = new THREE.Mesh(textGeometry, textMaterial);

    textMesh.position.set(0, -width / 2, 0);

    const bender = new Bender();
    bender.bend(textGeometry, "z", Math.PI / 110);

    mesh.add(textMesh);
}